import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown } from "lucide-react";
import { POPOVER_VIEWPORT_PAD } from "./popoverLayout";

/**
 * Themed single-select popover. Replaces native `<select>` in
 * canvas surfaces and composer so the OPEN options list isn't
 * OS-native chrome (which `appearance: none` can't reach).
 *
 * The popover is portaled to `document.body` and positioned with
 * `fixed` coords anchored to the trigger, mirroring TkticDateInput
 * so a scroll-clipping ancestor (e.g. the focus-mode tools panel)
 * doesn't crop it.
 *
 * Controlled component: pass `value` + `onChange(nextValue)`.
 */

export type TkticSelectOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

export type TkticSelectProps = {
  value: string;
  onChange: (next: string) => void;
  options: TkticSelectOption[];
  disabled?: boolean;
  placeholder?: string;
  /** ARIA label for the trigger. */
  ariaLabel?: string;
  id?: string;
  name?: string;
  "data-testid"?: string;
  className?: string;
  /** Trigger + option font size. `sm` = 12px (text-xs) for dense
   * surfaces like admin tables; `md` = 14px (text-sm) for modals and
   * forms with more breathing room. Defaults to `md` so the canvas /
   * coach-review surfaces that adopted this component before the prop
   * existed render unchanged. */
  size?: "sm" | "md";
  /** Optional override for the trigger's display label (defaults to
   * the matching option's `label`, or `placeholder` when no match). */
  renderTriggerLabel?: (opt: TkticSelectOption | undefined) => React.ReactNode;
  /** Extra attributes (e.g. `data-canvas-game-format`) forwarded to
   * the hidden mirror `<select>`. The mirror is the element existing
   * Playwright `selectOption()` / `toHaveValue()` assertions target
   * via attribute selectors, so canvas-specific data-attrs must land
   * there, not on a wrapper div. Use a permissive record so callers
   * don't need to cast for `data-*`. */
  nativeSelectProps?: Record<string, string | number | boolean> & {
    style?: CSSProperties;
  };
};

export function TkticSelect({
  value,
  onChange,
  options,
  disabled = false,
  placeholder = "Select…",
  ariaLabel,
  id,
  name,
  "data-testid": dataTestId,
  className = "",
  size = "md",
  renderTriggerLabel,
  nativeSelectProps,
}: TkticSelectProps) {
  // 12px for dense surfaces, 14px otherwise. Applied to both the
  // trigger and every option button so the closed + open states match.
  const textSizeClass = size === "sm" ? "text-xs" : "text-sm";
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const popupId = useId();
  // Active descendant for keyboard navigation. Resets when the
  // popover opens (highlights the currently-selected option, or the
  // first option if nothing is selected).
  const [activeIdx, setActiveIdx] = useState<number>(-1);
  // Type-ahead buffer (cleared after a short idle window).
  const typeBufRef = useRef<{ text: string; until: number }>({ text: "", until: 0 });

  const selected = useMemo(
    () => options.find((o) => o.value === value),
    [options, value],
  );

  useEffect(() => {
    if (!open) {
      setActiveIdx(-1);
      return;
    }
    const startIdx = Math.max(
      0,
      options.findIndex((o) => o.value === value && !o.disabled),
    );
    setActiveIdx(startIdx);
  }, [open, options, value]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (popupRef.current?.contains(target)) return;
      setOpen(false);
    };
    window.addEventListener("mousedown", onPointer);
    return () => window.removeEventListener("mousedown", onPointer);
  }, [open]);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const compute = () => {
      const el = triggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const viewportPad = POPOVER_VIEWPORT_PAD;
      setPos({
        top: r.bottom + 4,
        left: Math.max(viewportPad, Math.min(r.left, window.innerWidth - r.width - viewportPad)),
        width: Math.min(Math.max(r.width, 160), window.innerWidth - viewportPad * 2),
      });
    };
    compute();
    window.addEventListener("scroll", compute, true);
    window.addEventListener("resize", compute);
    return () => {
      window.removeEventListener("scroll", compute, true);
      window.removeEventListener("resize", compute);
    };
  }, [open]);

  const triggerLabel = renderTriggerLabel
    ? renderTriggerLabel(selected)
    : (selected?.label ?? (
        <span className="text-ink-muted">{placeholder}</span>
      ));

  // Active option id (for `aria-activedescendant`). When the popup
  // is open, keyboard focus stays on the trigger and the listbox
  // advertises which option is "virtually" focused via this id.
  const activeOptionId =
    open && activeIdx >= 0 && activeIdx < options.length
      ? `${popupId}-opt-${options[activeIdx].value}`
      : undefined;

  // Move the highlight by `delta` positions, skipping disabled
  // options. Wraps at the ends. Returns the new index.
  const moveActive = (from: number, delta: 1 | -1): number => {
    if (options.length === 0) return -1;
    let i = from;
    for (let step = 0; step < options.length; step++) {
      i = (i + delta + options.length) % options.length;
      if (!options[i].disabled) return i;
    }
    return from;
  };

  const handleTypeahead = (ch: string) => {
    const now = Date.now();
    const buf =
      now < typeBufRef.current.until
        ? typeBufRef.current.text + ch
        : ch;
    typeBufRef.current = { text: buf, until: now + 500 };
    const match = options.findIndex(
      (o) => !o.disabled && o.label.toLowerCase().startsWith(buf.toLowerCase()),
    );
    if (match >= 0) setActiveIdx(match);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    // Closed-state keys: open the popup.
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    // Open-state keys.
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActiveIdx((i) => moveActive(i < 0 ? -1 : i, 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveIdx((i) => moveActive(i < 0 ? options.length : i, -1));
        break;
      case "Home":
        e.preventDefault();
        setActiveIdx(moveActive(-1, 1));
        break;
      case "End":
        e.preventDefault();
        setActiveIdx(moveActive(options.length, -1));
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        if (activeIdx >= 0 && !options[activeIdx].disabled) {
          onChange(options[activeIdx].value);
          setOpen(false);
        }
        break;
      case "Escape":
        e.preventDefault();
        setOpen(false);
        break;
      case "Tab":
        // Don't preventDefault — let Tab move focus naturally; just
        // close the listbox so we don't strand the popup open.
        setOpen(false);
        break;
      default:
        if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
          handleTypeahead(e.key);
        }
    }
  };

  return (
    <>
      {/* Hidden native <select> kept in sync with the controlled
       * value. Lets Playwright's `selectOption()` /
       * `toHaveValue()` keep working on the same data-testid the
       * old native <select> used (visible button gets a `-trigger`
       * suffixed testid). Also ensures form serialization /
       * `FormData` keep including this field when the wrapper is
       * inside an uncontrolled `<form>`. `nativeSelectProps`
       * forwards canvas-specific data-attrs (e.g.
       * `data-canvas-game-format`) so attribute-based test
       * selectors keep matching. */}
      <select
        {...nativeSelectProps}
        aria-hidden="true"
        tabIndex={-1}
        name={name}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        data-testid={dataTestId}
        style={{
          position: "absolute",
          opacity: 0,
          pointerEvents: "none",
          width: 1,
          height: 1,
          overflow: "hidden",
          clip: "rect(0 0 0 0)",
          ...(nativeSelectProps?.style ?? {}),
        }}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value} disabled={opt.disabled}>
            {opt.label}
          </option>
        ))}
      </select>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? popupId : undefined}
        aria-activedescendant={activeOptionId}
        aria-label={ariaLabel}
        data-testid={dataTestId ? `${dataTestId}-trigger` : undefined}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={handleKeyDown}
        className={`flex w-full items-center justify-between gap-2 rounded border border-rule bg-canvas px-2 py-1.5 ${textSizeClass} text-ink hover:border-accent/60 focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50 transition-colors ${className}`}
      >
        <span className="truncate text-left">{triggerLabel}</span>
        <ChevronIcon open={open} />
      </button>
      {open && pos && typeof document !== "undefined"
        ? createPortal(
            <div
              ref={popupRef}
              role="listbox"
              id={popupId}
              aria-label={ariaLabel}
              aria-activedescendant={activeOptionId}
              data-tktic-popover="select"
              data-testid={dataTestId ? `${dataTestId}-popup` : undefined}
              style={{
                position: "fixed",
                top: pos.top,
                left: pos.left,
                minWidth: pos.width,
              }}
              className="z-[200] max-h-[min(18rem,calc(100dvh-1rem))] max-w-[calc(100vw-1rem)] overflow-y-auto rounded border border-rule bg-surface shadow-lg py-1"
            >
              {options.map((opt, idx) => {
                const isSelected = opt.value === value;
                const isActive = idx === activeIdx;
                return (
                  <button
                    key={opt.value}
                    id={`${popupId}-opt-${opt.value}`}
                    type="button"
                    role="option"
                    tabIndex={-1}
                    ref={
                      isActive
                        ? (el) => {
                            el?.scrollIntoView({ block: "nearest" });
                          }
                        : undefined
                    }
                    aria-selected={isSelected}
                    disabled={opt.disabled}
                    onMouseEnter={() => setActiveIdx(idx)}
                    onClick={() => {
                      onChange(opt.value);
                      setOpen(false);
                    }}
                    data-testid={
                      dataTestId ? `${dataTestId}-option-${opt.value}` : undefined
                    }
                    className={[
                      `flex w-full items-center justify-between gap-2 px-2 py-1.5 text-left ${textSizeClass} transition-colors`,
                      isActive ? "bg-accent/20" : "",
                      isSelected && !isActive ? "bg-accent/10" : "",
                      "text-ink",
                      opt.disabled ? "opacity-40 cursor-not-allowed" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    <span className="truncate">{opt.label}</span>
                    {isSelected ? (
                      <span className="text-accent" aria-hidden="true">
                        <Check size={14} strokeWidth={2.4} />
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <ChevronDown
      size={12}
      strokeWidth={2}
      aria-hidden="true"
      style={{
        transition: "transform 120ms ease",
        transform: open ? "rotate(180deg)" : "rotate(0deg)",
      }}
    />
  );
}
